import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getJCJPIDs } from "../../../utils/common/index";
import { triggerFetchSimActMetricsAPI } from "../../../redux/actions/activationSales";
import RechargeSalesInsightsView from "../../../views/rechargeSales/insights/rechargeSalesInsightsView";
import {
  MDN_SECTION,
  TILE_SIM_ACTIVATION,
  TILE_DAILY_ACTIVATING_OUTLET,
  DEFAULT_OUTLET_ACTIVATION_TYPE,
  buildSimActMetricsPayload,
  metricUrl,
  mapSimConnectivityInsightKeys,
  INSIGHTS_METRIC_TYPES,
  filterActivationTypeOptionsByCount,
} from "../../../utils/simActivationInsights";

const PREFIX_ACT = "ACT_";

function isEligibleTile(tileName) {
  if (!tileName) return false;
  return (
    tileName === TILE_SIM_ACTIVATION ||
    tileName === TILE_DAILY_ACTIVATING_OUTLET
  );
}

export default function SimConnectivityInsights({
  selectedTab,
  visibleTiles,
  filterValue,
  moduleName,
}) {
  const dispatch = useDispatch();
  const { shouldRun, headerSelectedDateFrom, headerSelectedDateTo } = useSelector(
    (state) => state.homeReducer
  );
  const { simActMetricsInsights, simActivationJiophoneObj } = useSelector(
    (state) => state.activationSalesReducer
  );
  const [selectedActivationType, setSelectedActivationType] =
    useState("Jiophone");
  const previousFetchKeyRef = useRef(null);

  const { partnerRT } = getJCJPIDs(filterValue || {});

  const activationRows = simActivationJiophoneObj?.simActivationData || [];
  const jiophoneLoading = simActivationJiophoneObj?.loading;
  const jiophoneError = simActivationJiophoneObj?.error;

  const filteredActivationOptions = useMemo(
    () =>
      filterActivationTypeOptionsByCount(
        activationRows,
        jiophoneLoading,
        jiophoneError
      ),
    [activationRows, jiophoneLoading, jiophoneError]
  );

  const currentTile =
    visibleTiles && visibleTiles.length
      ? visibleTiles[selectedTab]
      : undefined;
  const tileName = currentTile && currentTile.tileName;
  const eligible = isEligibleTile(tileName);

  const isSimActivation = tileName === TILE_SIM_ACTIVATION;
  const noActivationTypesForInsights =
    isSimActivation &&
    !jiophoneLoading &&
    activationRows.length > 0 &&
    filteredActivationOptions.length === 0;

  useEffect(() => {
    if (!isSimActivation || !filteredActivationOptions.length) return;
    if (
      !filteredActivationOptions.some((o) => o.key === selectedActivationType)
    ) {
      setSelectedActivationType(filteredActivationOptions[0].key);
      previousFetchKeyRef.current = null;
    }
  }, [isSimActivation, filteredActivationOptions, selectedActivationType]);

  useEffect(() => {
    if (!shouldRun || !filterValue || !eligible || !moduleName) {
      previousFetchKeyRef.current = null;
      return;
    }

    const isSimActivationTile = tileName === TILE_SIM_ACTIVATION;
    if (isSimActivationTile && noActivationTypesForInsights) {
      previousFetchKeyRef.current = null;
      return;
    }

    const activationTypeForApi = isSimActivationTile
      ? selectedActivationType
      : DEFAULT_OUTLET_ACTIVATION_TYPE;

    const partnerId =
      filterValue.orgId && filterValue.orgId !== ""
        ? filterValue.orgId
        : (filterValue.selectedRole &&
            filterValue.selectedRole.split(" - ")[2]) ||
          "";
    const fetchKey = `${partnerId}_${filterValue.selectedPartnerRoleName || ""}_${tileName}_${activationTypeForApi}_${moduleName}_${headerSelectedDateFrom || ""}_${headerSelectedDateTo || ""}`;
    if (previousFetchKeyRef.current === fetchKey) return;
    previousFetchKeyRef.current = fetchKey;

    INSIGHTS_METRIC_TYPES.forEach(({ key: metricKey }) => {
      const url = metricUrl(metricKey);
      if (!url) return;
      const params = buildSimActMetricsPayload(filterValue, partnerRT, {
        moduleName,
        section: MDN_SECTION,
        tile: tileName,
        activationType: activationTypeForApi,
        metricType: metricKey,
      });

      let extraData;
      if (isSimActivationTile) {
        extraData = `${PREFIX_ACT}${selectedActivationType}_${metricKey}`;
      } else {
        extraData = `OUTLET_${metricKey}`;
      }

      dispatch(
        triggerFetchSimActMetricsAPI({
          headers: { "Content-Type": "application/json" },
          url,
          params,
          extraData,
        })
      );
    });
  }, [
    dispatch,
    eligible,
    filterValue,
    moduleName,
    partnerRT,
    selectedActivationType,
    shouldRun,
    tileName,
    noActivationTypesForInsights,
    headerSelectedDateFrom,
    headerSelectedDateTo,
  ]);

  if (!eligible) {
    return null;
  }

  const sectionKeyForView = isSimActivation
    ? selectedActivationType
    : "OUTLET";

  const selectedActivationLabel =
    filteredActivationOptions.find((o) => o.key === selectedActivationType)
      ?.label || selectedActivationType;

  const insightsContextName = isSimActivation
    ? noActivationTypesForInsights
      ? TILE_SIM_ACTIVATION
      : `${TILE_SIM_ACTIVATION} — ${selectedActivationLabel}`
    : TILE_DAILY_ACTIVATING_OUTLET;

  const mapped = mapSimConnectivityInsightKeys(
    simActMetricsInsights.data,
    simActMetricsInsights.loadingKeys,
    isSimActivation ? "simActivation" : "dailyOutlet"
  );
  const mappedInsights = {
    ...simActMetricsInsights,
    data: mapped.data,
    loadingKeys: mapped.loadingKeys,
  };

  return (
    <div className="col-12 mt-3">
      {isSimActivation ? (
        filteredActivationOptions.length ? (
          <ul className="nav nav-pills tabbtndiv text-center mb-2">
            {filteredActivationOptions.map((opt) => (
              <li key={opt.key}>
                <a
                  href={`#mdn-act-${opt.key}`}
                  className={
                    selectedActivationType === opt.key ? "active" : ""
                  }
                  onClick={(e) => {
                    e.preventDefault();
                    setSelectedActivationType(opt.key);
                    previousFetchKeyRef.current = null;
                  }}
                >
                  {opt.label}
                </a>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-muted small mb-2">
            No activation categories with volume — insights are hidden until at
            least one activation type has a count greater than zero.
          </p>
        )
      ) : null}
      {!(isSimActivation && noActivationTypesForInsights) ? (
        <RechargeSalesInsightsView
          rechargeInsights={mappedInsights}
          filterValue={filterValue}
          section={sectionKeyForView}
          insightsContextLabel={
            <strong>{insightsContextName}</strong>
          }
        />
      ) : null}
    </div>
  );
}
